/**
 * The live session view — store-bound composition of the player-ui pieces
 * (architecture.md §9; R7).
 *
 * @remarks
 * Bound with `useSyncExternalStore` directly: the tree is three components
 * deep, so `react-redux` would be a dependency in search of a problem —
 * it arrives when the component tree earns it (Phase 2+). Selector results
 * are referentially stable (game-store's contract), so subscription renders
 * stay cheap.
 *
 * UI-transient data stays OUT of the store on purpose: the correction echo
 * ("reading crowbat as crowbar") renders from the last outcome's
 * `corrections` via local state. The QUESTION surface renders from the
 * view's `pendingQuestion` (D79) — its chips SUBMIT the candidate's name as
 * the player's own answer (§8.7), one path with typing.
 *
 * D156: everything this component renders is the SESSION VIEW the host
 * handed the store — the map, the journal, the voices, the scene, the bed,
 * the clock, the chips all arrive derived. This file computes nothing from
 * a world, because in `play: 'server'` mode there is no world here to
 * compute from; the projection lives in `@orpheus/player-host` and runs
 * wherever the turn ran.
 *
 * @packageDocumentation
 */

import type { GameDescription, TokenCorrectionView } from '@orpheus/api-contracts';
import type { PlayHost } from '@orpheus/player-host';

import {
	type GameStore,
	historyCursorChanged,
	inputValueChanged,
	selectAmbientAudioVolume,
	selectAutoWaitPreference,
	selectCommandHistory,
	selectHistoryBrowseValue,
	selectHistoryCursorIndex,
	selectInputValue,
	selectIsAmbientAudioEnabled,
	selectIsRoomImageryEnabled,
	selectIsSessionReady,
	selectMapStylePreference,
	selectPendingPort,
	selectPendingQuestion,
	selectSessionView,
	selectTranscriptEntries,
	selectTurnCount,
	submitPlayerInput,
} from '@orpheus/game-store';
import { CommandInputComponent, planTranscriptReveal, RoomImageComponent, TranscriptListComponent } from '@orpheus/player-ui';
import { type ReactNode, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { type AmbiencePlayer } from './ambience-player.js';
import { SessionExtras } from './session-extras.js';
import { cueTimingOf, pinViewportToNewestTurn, readView, type WallAnswer, withWallAnswer } from './session-view-support.js';
import { type PurchaseArrival, type StoreClient } from './store-client.js';
import { useAmbienceDriver } from './use-ambience-driver.js';
import { isClockEnabledFor, usePacingDriver } from './use-pacing-driver.js';

/** R27/D99 §4: how long a room change's curtain holds the arriving prose (and, D265, its sounds) back. */
const PLATE_CURTAIN_MS = 280;

/** Props for {@link SessionViewComponent}. */
export interface SessionViewComponentProps {
	/** The account control (D157), when the build has an identity Worker; passed through to the control row. */
	readonly account?: ReactNode;
	/**
	 * R35: the shell's ambience player, shared with the title screen so the
	 * theme can crossfade into the first bed. Optional: a view without one
	 * (unit tests, embeddings) renders identically and plays nothing.
	 */
	readonly ambiencePlayer?: AmbiencePlayer;
	/** The game around the session — fixed for its life (D156). */
	readonly game: GameDescription;
	/** The host running the session — the export asks it for the transcript (D156). */
	readonly host: PlayHost;
	/**
	 * The app's masthead, rendered at the head of the SCROLLBACK rather than
	 * above the plate — so it scrolls away with the opening beats and gives its
	 * vertical space back to the picture and the prose.
	 *
	 * D130 removed the alternative. D121 had put a copy in a RAIL beside the
	 * picture on wide screens, which cost the picture 288px of column at every
	 * desktop width — 25–35% of it — and made the picture SHRINK crossing the
	 * rail's breakpoint while the window grew. A masthead is not worth a third
	 * of the picture, and scrolling away gives the same vertical space back that
	 * standing beside was buying.
	 */
	readonly masthead?: ReactNode;
	/** The session's store. */
	readonly store: GameStore;
	/** The store Worker's client (D165), when the build names one — the paywall's Buy button; absent, the words stand alone. */
	readonly storefront?: StoreClient;
	/** What the browser came back from a checkout with (D165), read once by the app. */
	readonly purchaseArrival?: PurchaseArrival;
}

/**
 * Render the playable session: transcript viewport, correction echo, and the
 * command line with clarification chips.
 *
 * @param props - {@link SessionViewComponentProps}
 * @returns The rendered session
 */
export const SessionViewComponent = ({ account, ambiencePlayer, game, host, masthead, purchaseArrival, store, storefront }: SessionViewComponentProps) => {
	// Stable arrow wrappers (memoized per store): `useSyncExternalStore` needs
	// referentially stable functions or it resubscribes every render, and the
	// unbound-method rule rightly refuses raw method references.
	const storeBinding = useMemo(
		() => ({
			getState: () => store.getState(),
			subscribe: (onStoreChange: () => void) => store.subscribe(onStoreChange),
		}),
		[store],
	);
	const storeState = useSyncExternalStore(storeBinding.subscribe, storeBinding.getState);
	const [lastCorrections, setLastCorrections] = useState<readonly TokenCorrectionView[]>([]);
	// D165: the turn the edge of the demo refused — drawn as the transcript's newest turn until the next one answers.
	const [lastWall, setLastWall] = useState<WallAnswer | undefined>(undefined);
	const transcriptViewportRef = useRef<HTMLDivElement>(null);

	const recordedEntries = selectTranscriptEntries(storeState);
	const turnCount = selectTurnCount(storeState);
	// Memoized: the viewport pins on every new array, and a wall drawn afresh each render would pin on every render.
	const transcriptEntries = useMemo(() => withWallAnswer(recordedEntries, lastWall, turnCount), [recordedEntries, lastWall, turnCount]);

	// R27 (D100): the reveal is for ARRIVAL. A restored session's backlog is
	// prose already experienced, so the boundary starts at whatever the first
	// render found; and any keystroke COMPLETES the running reveal (Calder's
	// blocking requirement — an impatient player never notices the effect
	// exists, a patient one gets all of it), so the boundary advances to the
	// present the moment the player types. Presentation-local throughout.
	const initialEntryCountRef = useRef<number | undefined>(undefined);

	initialEntryCountRef.current ??= transcriptEntries.length;

	const [settledEntryCount, setSettledEntryCount] = useState(0);
	const revealFromIndex = Math.max(initialEntryCountRef.current, settledEntryCount);
	const settleReveal = () => {
		setSettledEntryCount(transcriptEntries.length);
	};
	const isRoomImageryEnabled = selectIsRoomImageryEnabled(storeState);
	const commandHistory = selectCommandHistory(storeState);
	const historyCursorIndex = selectHistoryCursorIndex(storeState);
	// R34 (D114): the question renders whenever the engine holds one — there is
	// no presentation-only dismissal any more. The chip's ✕ WITHDRAWS: it
	// submits `never mind` through the same door the keyboard uses, the engine
	// closes the question with a spoken line, and this selector goes empty
	// because the question is actually gone. Calder's §7.1: a posted exit must
	// open.
	const visibleQuestion = selectPendingQuestion(storeState);
	const displayedValue = selectHistoryBrowseValue(storeState) ?? selectInputValue(storeState);
	const view = selectSessionView(storeState);
	const { activeRoomKey, ambience, pacing, scene, speakers, suggestions, transcriptLength } = readView(view, isRoomImageryEnabled);

	// R26: the dissolve's pace depends on WHY the plate changed — a room change
	// is a cut and wants to be quick; an in-room change (a breaker flipping the
	// plate, the Images toggle) is a thing happening to a standing player and
	// can afford to be slower. Presentation-local: a ref remembers the room the
	// last render saw, and the comparison never touches committed state.
	const previousRoomKeyRef = useRef(activeRoomKey);
	const plateTransitionKind = activeRoomKey === previousRoomKeyRef.current ? ('in-room' as const) : ('room-change' as const);

	useEffect(() => {
		previousRoomKeyRef.current = activeRoomKey;
	}, [activeRoomKey]);

	// R27/D99 §4: the image leads and the text follows — when this turn's plate change was a room change, the arriving
	// prose waits out the curtain before its reveal begins (and, D265, so do the sounds its lines make).
	const revealBaseDelayMs = scene !== undefined && plateTransitionKind === 'room-change' ? PLATE_CURTAIN_MS : 0;

	// R23/R32 (D89, D96): the room's picture is the view's — WHICH plate and
	// which sprites are true is the engine's judgment, made wherever the turn
	// ran; the shell only frames it, above the prose, under the Images toggle.

	// R35 (D115): the room's sound — the view's target, driven in its own
	// hook. D116 adds the loudness lever beside the on/off one.
	const isAmbientAudioEnabled = selectIsAmbientAudioEnabled(storeState);
	const ambientAudioVolume = selectAmbientAudioVolume(storeState);

	useAmbienceDriver(ambiencePlayer, ambience, isAmbientAudioEnabled, ambientAudioVolume);

	// D265: a turn's sounds, heard as it arrived, play with their lines — timed by the same reveal plan the list draws.
	useEffect(() => {
		ambiencePlayer?.playHeard(cueTimingOf(transcriptEntries, revealFromIndex, speakers, revealBaseDelayMs));
	}, [ambiencePlayer, transcriptEntries, revealFromIndex, speakers, revealBaseDelayMs]);

	// D265: and the game's cues are fetched ahead, so the first one sounds on its line too.
	useEffect(() => {
		ambiencePlayer?.prefetch(game.cues ?? []);
	}, [ambiencePlayer, game.cues]);

	// D153: the clock — the pack's pace under the player's word, driven in its
	// own hook. The reveal plan is the list's own (it re-derives the same
	// one); the driver reads its total so a tick never lands mid-sentence.
	const autoWaitPreference = selectAutoWaitPreference(storeState);
	const isClockEnabled = isClockEnabledFor(game.clock, autoWaitPreference);
	const { mutedEntryIds, turnStartedAt } = usePacingDriver({
		inputValue: displayedValue,
		isClockEnabled,
		pacing,
		revealMs: planTranscriptReveal(transcriptEntries, revealFromIndex, speakers).totalMs,
		store,
		transcriptLength,
	});

	/*
	 * The shell owns the viewport: pin it to the newest turn as turns LAND — the
	 * opening included (D130, Mike's ruling).
	 *
	 * This used to skip the first paint, on the reasoning that "a session opens
	 * at its beginning" and an unconditional pin would scroll the title away
	 * before it was read. What that missed is how little of the beginning is on
	 * screen: the transcript is the shortest thing in the column — 111px at
	 * 1280x800, against 340px of opening — so arriving without a pin left most
	 * of the opening below the fold with nothing to say it was there. A viewport
	 * that never moves on arrival does not read as "start at the top"; it reads
	 * as broken.
	 *
	 * Nothing is skipped by pinning. `pinViewportToNewestTurn` aligns the newest
	 * turn's TOP whenever that turn is taller than the viewport, which the
	 * opening always is, so the scroll lands on the opening's first line rather
	 * than its last. The masthead above it scrolls off — which is what putting
	 * it in the scrollback was for, and it is on screen for the length of the
	 * animation on the way past.
	 */
	useEffect(() => {
		const viewport = transcriptViewportRef.current;

		if (viewport !== null) {
			pinViewportToNewestTurn(viewport);
		}
	}, [transcriptEntries]);

	// F8 (Calder's field study): blank input REACHES the engine — the pack
	// authors a `blankInput` line, and pressing Enter with nothing to say is a
	// thing players do when they are stuck. The engine already treats blanks
	// as real inputs (D09); it was the shell that swallowed them.
	const submitAnswer = (rawInput: string) => {
		// The host answers when the turn has run — here, at once; on the
		// platform, a round trip later. A refused turn keeps the last echo.
		void store.dispatch(submitPlayerInput(rawInput)).then((outcome) => {
			setLastCorrections(outcome.corrections);
			setLastWall(outcome.paywall === undefined ? undefined : { input: rawInput, lines: outcome.paywall });
		}, () => undefined);
	};

	const submitDisplayedValue = () => {
		submitAnswer(displayedValue);
	};

	const browseHistory = (step: -1 | 1) => {
		// R27: browsing history is a keystroke too — the reveal completes.
		settleReveal();

		if (commandHistory.length === 0) {
			return;
		}

		if (step === -1) {
			// Older: start at the newest entry, stop at the oldest.
			const nextIndex =
				historyCursorIndex === undefined ? commandHistory.length - 1 : Math.max(0, historyCursorIndex - 1);

			store.dispatch(historyCursorChanged(nextIndex));

			return;
		}

		// Newer: walk forward; past the newest entry means back to a blank line.
		if (historyCursorIndex === undefined) {
			return;
		}

		store.dispatch(
			historyCursorChanged(historyCursorIndex + 1 >= commandHistory.length ? undefined : historyCursorIndex + 1),
		);
	};

	/*
	 * D121 (Mike): when a plate is actually SHOWING, the shell trades reading
	 * room for picture room — the masthead moves to a left rail on tablet-and-
	 * up, the prose and prompt drop a type size, and the shell column widens
	 * (all in CSS, keyed off this one class). Images off, or a plate-less
	 * room, and none of it applies: the pure-text game keeps its pure-text
	 * shell.
	 */
	const isImagedLayout = scene !== undefined;

	return (
		<section className={`session-view${isImagedLayout ? ' session-view--imaged' : ''}`}>
			{/* D121: the rail's copy of the masthead — shown by CSS only on
			    tablet-and-up while imaged; the in-scrollback copy hides in exactly
			    that state, so precisely one title is ever visible. */}
			<div className="session-view__main">
				{/* R26: rendered unconditionally — the component owns the dissolve,
				    including the fade to black when the plate LEAVES (a plate-less
				    room, or the Images toggle), so an absent plate is an instruction
				    rather than an unmount. */}
				<RoomImageComponent
					aspectRatio={game.imageAspectRatio}
					plate={scene?.plate}
					sprites={scene?.sprites ?? []}
					transitionKind={plateTransitionKind}
				/>
				{/* Focusable so keyboard users can scroll the history — axe's
			    scrollable-region-focusable REQUIRES the tabindex the static rule
			    flags (the rule cannot see the overflow); pinned by the R8 E2E
			    scan, which fails if this is "fixed". */}
				{/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- scrollable region, see above */}
				<div
					aria-label="Transcript scrollback"
					className="session-view__transcript"
					ref={transcriptViewportRef}
					role="region"
					// The curtain (above), as a presentation variable the reveal animations' delay calc reads.
					style={{ ['--reveal-base-delay' as string]: `${String(revealBaseDelayMs)}ms` }}
					tabIndex={0}
				>
					{/* D121: hidden by CSS while the rail shows the title instead. */}
					<div className="session-view__masthead-inline">{masthead}</div>
					<TranscriptListComponent entries={transcriptEntries} mutedEntryIds={mutedEntryIds} revealFromIndex={revealFromIndex} speakers={speakers} />
				</div>
				{/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
				{/* The map and the journal are REFERENCES, not part of the reading
			    surface: they live behind buttons in the control row so neither can
			    push the prompt down the page as the world (or the shelf) grows.
			    The row is its own component (D153, at the file-size cap). */}
				{view === undefined ? undefined : (
					<SessionExtras
						account={account}
						ambientAudioVolume={ambientAudioVolume}
						game={game}
						host={host}
						isAmbientAudioEnabled={isAmbientAudioEnabled}
						isClockEnabled={isClockEnabled}
						isRoomImageryEnabled={isRoomImageryEnabled}
						mapStylePreference={selectMapStylePreference(storeState)}
						onRestart={() => {
							setLastCorrections([]);
							setLastWall(undefined);
						}}
						isWalled={lastWall !== undefined}
						pendingPort={selectPendingPort(storeState)}
						purchaseArrival={purchaseArrival}
						store={store}
						storefront={storefront}
						turnStartedAt={turnStartedAt}
						view={view}
					/>
				)}
				{lastCorrections.length > 0 ? (
					<p className="session-view__corrections">
						(reading{' '}
						{lastCorrections
							.map((correction) => `${correction.originalText} as ${correction.correctedText}`)
							.join(', ')}
						)
					</p>
				) : undefined}
				<CommandInputComponent
					isEnabled={selectIsSessionReady(storeState)}
					onHistoryDown={() => {
						browseHistory(1);
					}}
					onHistoryUp={() => {
						browseHistory(-1);
					}}
					onInputValueChange={(nextValue) => {
						// R27: any keystroke completes the running reveal — one state
						// change, not an interruption protocol (the mask constraint's
						// dividend).
						settleReveal();
						store.dispatch(inputValueChanged(nextValue));
					}}
					// R34/D114: dismissal IS withdrawal — the ✕ submits the directive as
					// the player's own words, so the transcript records the gesture and
					// replays reproduce it.
					onDismissQuestion={
						visibleQuestion === undefined
							? undefined
							: () => {
								submitAnswer('never mind');
							}
					}
					onSubmit={submitDisplayedValue}
					// D79/§8.7: a chip SUBMITS its answer as the player's own words —
					// one path with typing, so the echo and the history are honest.
					onSuggestionSelected={(suggestion) => {
						submitAnswer(suggestion.submitValue);
					}}
					// D79: the question is state; its text was resolved when it was
					// asked, so the surface and the transcript cannot disagree.
					promptQuestion={visibleQuestion?.questionText}
					suggestions={suggestions}
					value={displayedValue}
				/>
			</div>
		</section>
	);
};
